import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface ModalContextState {
  isModalOpen: string[];
  openModal: (modalName: string) => void;
  closeModal: (modalName: string) => void;
}

export const ModalContext = createContext<ModalContextState | undefined>(undefined);

export const ModalProvider: FC = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState<string[]>([]);

  const openModal = useCallback((modalName: string) => {
    setIsModalOpen((prev) => (prev.includes(modalName) ? prev : [...prev, modalName]));
  }, []);

  const closeModal = useCallback((modalName: string) => {
    setIsModalOpen((prev) => {
      const next = prev.filter((name) => name !== modalName);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  useEffect(() => {
    // Check if any modals are open by looking if the array is not empty
    const anyModalOpen = isModalOpen.length > 0;

    // Set the body overflow style based on whether any modal is open
    document.body.style.overflow = anyModalOpen ? 'hidden' : 'unset';

    // Cleanup function to unset the overflow when the component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const providerValue = useMemo(
    () => ({
      isModalOpen,
      openModal,
      closeModal,
    }),
    [closeModal, isModalOpen, openModal]
  );

  return <ModalContext.Provider value={providerValue}>{children}</ModalContext.Provider>;
};

export function useModal() {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return context;
}
